import { useState, useCallback, useMemo } from 'react';
import { GridPagination } from '../../../../types/datagrid';

interface UseGridPaginationParams {
  initialPagination: GridPagination;
  onPaginationChange?: (page: number, limit: number) => void;
  pageSizeOptions?: number[];
}

export const useGridPagination = ({
  initialPagination,
  onPaginationChange,
  pageSizeOptions = [10, 25, 50, 100],
}: UseGridPaginationParams) => {
  const [paginationState, setPaginationState] = useState<GridPagination>(initialPagination);

  // Handle page change
  const handlePageChange = useCallback(
    (newPage: number) => {
      const validPage = Math.max(1, Math.min(newPage, paginationState.totalPages));

      setPaginationState(prev => ({
        ...prev,
        page: validPage,
      }));

      onPaginationChange?.(validPage, paginationState.limit);
    },
    [paginationState.totalPages, paginationState.limit, onPaginationChange]
  );

  // Handle page size change
  const handleLimitChange = useCallback(
    (newLimit: number) => {
      const validLimit = Math.max(1, newLimit);

      // Calculate new page to maintain roughly the same position
      const currentStartIndex = (paginationState.page - 1) * paginationState.limit;
      const newPage = Math.max(1, Math.ceil((currentStartIndex + 1) / validLimit));

      setPaginationState(prev => ({
        ...prev,
        limit: validLimit,
        page: Math.min(newPage, Math.ceil(prev.total / validLimit) || 1),
        totalPages: Math.ceil(prev.total / validLimit) || 1,
      }));

      onPaginationChange?.(newPage, validLimit);
    },
    [paginationState.page, paginationState.limit, onPaginationChange]
  );

  // Go to first page
  const goToFirstPage = useCallback(() => {
    handlePageChange(1);
  }, [handlePageChange]);

  // Go to last page
  const goToLastPage = useCallback(() => {
    handlePageChange(paginationState.totalPages);
  }, [handlePageChange, paginationState.totalPages]);

  // Go to previous page
  const goToPreviousPage = useCallback(() => {
    handlePageChange(paginationState.page - 1);
  }, [handlePageChange, paginationState.page]);

  // Go to next page
  const goToNextPage = useCallback(() => {
    handlePageChange(paginationState.page + 1);
  }, [handlePageChange, paginationState.page]);

  // Calculate pagination info
  const paginationInfo = useMemo(() => {
    const { page, limit, total, totalPages } = paginationState;
    const startIndex = Math.max(0, (page - 1) * limit);
    const endIndex = Math.min(total, startIndex + limit);

    return {
      startIndex: startIndex + 1, // 1-based for display
      endIndex,
      currentPage: page,
      totalPages,
      total,
      limit,
      isFirstPage: page === 1,
      isLastPage: page === totalPages || totalPages === 0,
      hasPreviousPage: page > 1,
      hasNextPage: page < totalPages,
      isEmpty: total === 0,
      // Calculate visible page numbers for pagination UI
      visiblePages: calculateVisiblePages(page, totalPages),
    };
  }, [paginationState]);

  // Update pagination state from external source
  const updatePagination = useCallback((newPagination: Partial<GridPagination>) => {
    setPaginationState(prev => {
      const updated = { ...prev, ...newPagination };
      // Recalculate totalPages if total or limit changed
      if (newPagination.total !== undefined || newPagination.limit !== undefined) {
        updated.totalPages = Math.ceil(updated.total / updated.limit) || 1;
        // Ensure current page is valid
        updated.page = Math.min(updated.page, updated.totalPages);
      }
      return updated;
    });
  }, []);

  // Get pagination summary text
  const getPaginationSummary = useCallback(() => {
    const { startIndex, endIndex, total } = paginationInfo;

    if (total === 0) {
      return 'No results';
    }

    if (total === 1) {
      return '1 result';
    }

    if (startIndex === endIndex) {
      return `Result ${startIndex} of ${total}`;
    }

    return `Results ${startIndex}-${endIndex} of ${total}`;
  }, [paginationInfo]);

  // Generate page jump options
  const getPageJumpOptions = useCallback(() => {
    const { totalPages } = paginationState;
    const options = [];

    for (let i = 1; i <= Math.min(totalPages, 10); i++) {
      options.push({ value: i, label: `Page ${i}` });
    }

    if (totalPages > 10) {
      // Add some strategic page options
      const strategicPages = [
        Math.ceil(totalPages * 0.25),
        Math.ceil(totalPages * 0.5),
        Math.ceil(totalPages * 0.75),
        totalPages,
      ].filter((page, index, array) => page > 10 && array.indexOf(page) === index);

      strategicPages.forEach(page => {
        options.push({ value: page, label: `Page ${page}` });
      });
    }

    return options.sort((a, b) => a.value - b.value);
  }, [paginationState.totalPages]);

  return {
    paginationState,
    paginationInfo,
    pageSizeOptions,
    handlePageChange,
    handleLimitChange,
    goToFirstPage,
    goToLastPage,
    goToPreviousPage,
    goToNextPage,
    updatePagination,
    getPaginationSummary,
    getPageJumpOptions,

    // Convenience getters
    get currentPage() {
      return paginationState.page;
    },
    get pageSize() {
      return paginationState.limit;
    },
    get totalItems() {
      return paginationState.total;
    },
    get totalPages() {
      return paginationState.totalPages;
    },
    get hasResults() {
      return paginationState.total > 0;
    },
    get canGoBack() {
      return paginationInfo.hasPreviousPage;
    },
    get canGoForward() {
      return paginationInfo.hasNextPage;
    },
  };
};

// Helper function to calculate visible page numbers for pagination UI
function calculateVisiblePages(
  currentPage: number,
  totalPages: number,
  maxVisible: number = 7
): number[] {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const sideCount = Math.floor((maxVisible - 3) / 2); // -3 for first, current, last
  let start = Math.max(2, currentPage - sideCount);
  let end = Math.min(totalPages - 1, currentPage + sideCount);

  // Adjust if we're near the beginning or end
  if (currentPage - sideCount <= 2) {
    end = Math.min(totalPages - 1, maxVisible - 2);
  }

  if (currentPage + sideCount >= totalPages - 1) {
    start = Math.max(2, totalPages - maxVisible + 2);
  }

  const pages = [1];

  if (start > 2) {
    pages.push(-1); // Represents ellipsis
  }

  for (let i = start; i <= end; i++) {
    if (i !== 1 && i !== totalPages) {
      pages.push(i);
    }
  }

  if (end < totalPages - 1) {
    pages.push(-1); // Represents ellipsis
  }

  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
}
